import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ChevronDown, Sparkles } from "lucide-react";

const CALENDLY_LINK = "https://calendly.com/pragma-cx/discovery-call";

const stats = [
  { value: "14", label: "Business Days to Deploy", suffix: "" },
  { value: "60", label: "Cost Savings", suffix: "%" },
  { value: "100", label: "Scalable", suffix: "%" },
];

export default function Hero() {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springConfig = { damping: 25, stiffness: 150 };
  const springX = useSpring(mouseX, springConfig);
  const springY = useSpring(mouseY, springConfig);

  const handleMouseMove = (e) => {
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;
    mouseX.set((clientX / innerWidth - 0.5) * 50);
    mouseY.set((clientY / innerHeight - 0.5) * 50);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  };

  const handleScrollToFeatures = () => {
    const element = document.querySelector("#features");
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  return (
    <section 
      onMouseMove={handleMouseMove}
      className="min-h-screen relative overflow-hidden flex items-center pt-20 pb-12"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 grid-bg" />
      
      {/* Animated Background Shapes */}
      <motion.div
        className="absolute top-20 right-10 w-72 h-72 md:w-96 md:h-96 border border-cyan-400/20 rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="absolute bottom-32 left-10 w-56 h-56 md:w-80 md:h-80 border border-pink-500/20 rounded-full"
        animate={{ rotate: -360 }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="absolute top-1/3 right-1/4 w-32 h-32 border border-cyan-400/10"
        animate={{ rotate: 45, scale: [1, 1.1, 1] }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      
      {/* Gradient Orbs */}
      <motion.div 
        style={{ x: springX, y: springY }}
        className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" 
      />
      <motion.div 
        style={{ x: springX, y: springY }}
        className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" 
      />

      <div className="container relative z-10">
        <motion.div
          className="max-w-4xl mx-auto text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Badge */}
          <motion.div variants={itemVariants} className="mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-400/10 border border-cyan-400/30 text-cyan-400 text-sm font-mono rounded-full">
              <Sparkles className="w-4 h-4" />
              Elite Customer Service Outsourcing
            </span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1 variants={itemVariants} className="text-foreground mb-6">
            Customer Support That
            <br />
            <span className="text-cyan-400 text-glow-cyan">Scales With You</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            variants={itemVariants}
            className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto mb-10"
          >
            Professional support agents deployed in 14 business days. 60% cheaper than
            hiring in-house. Zero HR burden. Enterprise-grade quality.
          </motion.p>

          {/* Stats Row */}
          <motion.div
            variants={itemVariants}
            className="flex flex-wrap justify-center gap-8 md:gap-16 mb-12"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
              >
                <div className="text-4xl md:text-5xl font-mono font-bold text-cyan-400 text-glow-cyan">
                  {stat.value}
                  <span className="text-2xl md:text-3xl">{stat.suffix}</span>
                </div>
                <div className="text-sm text-muted-foreground/80 uppercase tracking-wider mt-1">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <motion.a
              href={CALENDLY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="neon-button group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="hero-cta-primary"
            >
              Start Free Consultation
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </motion.a>
            <motion.button
              onClick={() => {
                const element = document.querySelector("#pricing");
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" });
                }
              }}
              className="ghost-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="hero-cta-secondary"
            >
              View Pricing
            </motion.button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 cursor-pointer"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        onClick={handleScrollToFeatures}
      >
        <div className="flex flex-col items-center gap-2 text-muted-foreground/80 hover:text-cyan-400 transition-colors">
          <span className="text-xs uppercase tracking-widest">Scroll to explore</span>
          <motion.div
            className="w-6 h-10 border-2 border-current rounded-full flex items-start justify-center p-1"
          >
            <motion.div
              className="w-1.5 h-3 bg-current rounded-full"
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
